import React, { useEffect, useState, useMemo, useCallback, useLayoutEffect } from 'react';
import axios from 'axios';
import { DateRangeSelector, SelectField } from "../shared/Inputs";
import { format } from "date-fns";

import { hot } from 'react-hot-loader';

const numberFormat = new Intl.NumberFormat('en-US', {});

const formatMoney = (value) => {
  return value && `$${numberFormat.format(Math.round(parseFloat(value) / 100))}` || '-';
};

const PerformanceGraph = ({ data, goal }) => {
  const width = 248;
  const ringWidth = 24;
  const ringRadius = useMemo(() => width / 2 - ringWidth - ringWidth / 3, [width, ringWidth]);
  const ringCircumference = useMemo(() => ringRadius * 2 * Math.PI, [ringRadius]);
  const tickRadius = useMemo(() => width / 2 - ringWidth / 2 - 2, [width, ringWidth]);
  const tickCircumference = useMemo(() => tickRadius * 2 * Math.PI, [tickRadius]);
  const progress = useMemo(() => Math.min((data.powerboost_sales_completed || 0) / goal, 1.0), [data, goal]);
  const offset = useMemo(() => ringCircumference - progress * ringCircumference, [ringCircumference, progress]);

  const [animatedOffset, setAnimatedOffset] = useState(null);
  useEffect(() => {
    setAnimatedOffset(null);
    setTimeout(() => {
      setAnimatedOffset(offset);
    }, 1);
  }, [offset]);

  return (
    <div className="sales-ring-wrapper">
      <svg width={width} height={width} style={{marginTop: '24px'}}>
        <circle className="inner-ring-background" r={ringRadius} cx={width/2} cy={width/2 + 8} fill="transparent" strokeWidth={ringWidth}/>
        <circle className="inner-ring-progress" r={ringRadius} cx={width/2 - 8} cy={width/2}
          fill="transparent" strokeWidth={ringWidth} strokeDasharray={`${ringCircumference} ${ringCircumference}`}
          strokeDashoffset={animatedOffset !== null ? animatedOffset : ringCircumference}/>
        <circle className="ring-ticks" r={tickRadius} cx={width/2 - 8} cy={width/2}
          fill="transparent" strokeWidth="4"
          strokeDasharray={`2 ${tickCircumference/40 - 2}`}
        />
        <text className="ring-percentage" x="50%" y="50%" dy="28">{Math.round(progress * 100)}%</text>
        <text className="ring-zero" x="50%" y="12" dx="-6" textAnchor="end">${goal / 100000000}M</text>
        <text className="ring-zero" x="50%" y="12" dx="1" textAnchor="middle">|</text>
        <text className="ring-zero" x="50%" y="12" dx="7" textAnchor="start"> $0</text>
      </svg>
    </div>
  );
};

const SuiteTable = ({ data }) => {
  return (
    <table className="suite-table">
      <thead>
      <tr>
        <th colSpan="2">Suite Type</th>
        <th/>
        <th className="den">+ Den</th>
      </tr>
      </thead>
      <tbody>
      <tr>
        <td className="suite-type" colSpan={2}>Studio</td>
        <td className="percentage">
          {data.num_studio && `${Math.round(data.num_studio / data.deal_count * 100)}%` || '-'}
        </td>
        <td className="percentage">-</td>
      </tr>
      <tr>
        <td className="suite-type">1</td>
        <td className="suite-icon">
          <i className="material-icons">single_bed</i>
        </td>
        <td className="percentage">
          {data.num_bed1 && `${Math.round(data.num_bed1 / data.deal_count * 100)}%` || '-'}
        </td>
        <td className="percentage">
          {data.num_bed1_den && `${Math.round(data.num_bed1_den / data.deal_count * 100)}%` || '-'}
        </td>
      </tr>
      <tr>
        <td className="suite-type">2</td>
        <td className="suite-icon">
          <i className="material-icons">single_bed</i>
        </td>
        <td className="percentage">
          {data.num_bed2 && `${Math.round(data.num_bed2 / data.deal_count * 100)}%` || '-'}
        </td>
        <td className="percentage">
          {data.num_bed2_den && `${Math.round(data.num_bed2_den / data.deal_count * 100)}%` || '-'}
        </td>
      </tr>
      <tr>
        <td className="suite-type">3</td>
        <td className="suite-icon">
          <i className="material-icons">single_bed</i>
        </td>
        <td className="percentage">
          {data.num_bed3 && `${Math.round(data.num_bed3 / data.deal_count * 100)}%` || '-'}
        </td>
        <td className="percentage">
          {data.num_bed3_den && `${Math.round(data.num_bed3_den / data.deal_count * 100)}%` || '-'}
        </td>
      </tr>
      <tr>
        <td className="suite-type" colSpan={2}>PH</td>
        <td className="percentage">
          {data.num_penthouse && `${Math.round(data.num_penthouse / data.deal_count * 100)}%` || '-'}
        </td>
        <td className="percentage">-</td>
      </tr>
      </tbody>
    </table>
  );
};

const LeaderboardTable = ({ data }) => {
  useLayoutEffect(() => {
    window.refreshTips();
  }, null);

  return (
    <table className="leaderboard-table">
      <tbody>
      {data.leaderboard?.map((entry, i) => {
        return (
          <tr key={`${entry.agent_id}-${entry.team_id}`} onClick={() => Turbolinks.visit(entry.agent_id ? `/users/${entry.agent_id}` : `/users`)}>
            <td className="rank">{i + 1}</td>
            <td className="name">{entry.team_id ? <i className="material-icons">group</i> : null}{entry.agent_id ? `${entry.first_name} ${entry.last_name}` : entry.team_name}</td>
            <td className="sales" data-tippy-content={formatMoney(entry.total_sales)}>{formatMoney(entry.powerboost_total_sales)}</td>
          </tr>
        );
      })}
      </tbody>
    </table>
  );
};

const DashboardPage = ({ agents, projects, overall_goal }) => {
  const [ dateRange, setDateRange ] = useState(null);
  const [ agentFilter, setAgentFilter ] = useState(null);
  const [ projectFilter, setProjectFilter ] = useState(null);
  const [ data, setData ] = useState({});

  useEffect(() => {
    if (!dateRange) return;

    axios.get('/admin.json', {
      params: {
        start_date: format(dateRange.startDate, 'yyyy-MM-dd'),
        end_date: format(dateRange.endDate, 'yyyy-MM-dd'),
        agent_id: agentFilter && agentFilter.map(agent => agent.value),
        project_id: projectFilter && projectFilter.map(project => project.value),
      },
    }).then((response) => {
      setData(response.data);
    });
  }, [ dateRange, agentFilter, projectFilter ]);

  const agentOptions = useMemo(() => {
    return agents.map(agent => ({ label: agent.name, value: agent.id }));
  }, [ agents ]);

  const projectOptions = useMemo(() => {
    return projects.map(project => ({ label: project.name, value: project.id }));
  }, [ projects ]);

  return (
    <div id="admin-dashboard">
      <DateRangeSelector onChange={setDateRange} defaultType={{ label: 'Year-to-date', value: 'ytd' }}/>
      <div className="pure-g">
        <div className="pure-u-1 pure-u-md-1-2 pure-u-lg-1-3 pure-u-xl-1-4 form-padded">
          <SelectField label="Agents" options={agentOptions} value={agentFilter} onChange={setAgentFilter} isClearable={true} isMulti={true}/>
        </div>
        <div className="pure-u-1 pure-u-md-1-2 pure-u-lg-1-3 pure-u-xl-1-4 form-padded">
          <SelectField label="Projects" options={projectOptions} value={projectFilter} onChange={setProjectFilter} isClearable={true} isMulti={true}/>
        </div>
      </div>
      <div className="pure-g" style={{marginTop: '16px'}}>
        <div className="pure-u-1 pure-u-xl-7-12 goal-rings">
          <h2 className="metric-heading">System-wide performance</h2>
          <div className="goal-container">
            <PerformanceGraph data={data} goal={overall_goal}/>
            <div className="out-of-container">
              <h2 className="metric-heading">of</h2>
              <div className="metric-value">${overall_goal / 100000000} Million Goal</div>
            </div>
          </div>
        </div>
        <div className="sales-metrics pure-u-1 pure-u-lg-1-2 pure-u-xl-5-12">
          <div className="sales-metric">
            <div className="icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path fill="#C8B17A" d="M130.8 10.6c-.9 1.1-5.8 10.3-10.9 20.5-10.1 20.3-10.5 22.5-5 25.4 2.3 1.1 3.6 1.2 6 .4 2.7-.9 4.1-3.1 10.8-16.3l7.6-15.2 99.1 41.3c54.5 22.8 100.5 41.5 102.3 41.5 1.9.1 4-.6 5.3-1.7 2.6-2.3 2.7-7.6.1-9.9C343.3 94 141.2 9.9 136.5 9.3c-3.1-.4-4.4-.1-5.7 1.3zM37.1 61.8c-1.9 1.4-3.7 8.3-11.3 42.5l-9.1 40.9 2 2.6c2.6 3.6 8.1 4.2 10.8 1.2 1.3-1.4 4.2-12.6 9.6-37.2 4.4-19.3 8-35.2 8.2-35.4.1-.2 75.2 16.6 166.7 37.1 91.6 20.6 167.9 37.5 169.7 37.5 7.4 0 9.9-9.5 3.4-12.9C385 136.9 44 60 41.1 60c-1 0-2.9.8-4 1.8zM2.5 173.5 0 175.9v210.2l2.5 2.4 2.4 2.5h147.9c147.4 0 147.8 0 150-2 2.9-2.7 2.9-8 .1-10.6-2.1-1.8-6.1-1.9-145-2.2L15 376V186h413v10.5c0 11.8 1.3 14.5 7 14.5a8 8 0 0 0 5-2c1.8-1.8 2-3.3 2-17.9v-16l-2.6-2-2.7-2.1H4.9l-2.4 2.5z"/>
                <path fill="#C8B17A" d="M76.5 206.5c-1.7.9-11.8 10.3-22.5 21L34.5 247l-.3 32.5c-.2 25 0 33 1 34.9 2.2 4.1 36.2 40.8 38.6 41.7 1.3.5 18 .9 37.1.9 33.7 0 34.9-.1 37.5-2.1 1.9-1.5 2.6-3 2.6-5.4 0-2.4-.7-3.9-2.6-5.4-2.6-2-3.8-2.1-35.9-2.1H79.3l-15.1-16.7L49 308.7v-55.6l16.8-16.5 16.7-16.5 32.1-.1c30.8 0 32.1-.1 34.2-2 2.9-2.7 2.9-8 .1-10.6-2-1.8-4.3-1.9-35.8-2.1-28.4-.3-34.1-.1-36.6 1.2zM279 205.7c-5.6 2.1-6.8 8.7-2.4 12.2 2.6 2 3.8 2.1 38.4 2.1s35.8-.1 38.4-2.1c3.1-2.5 3.4-6.6.7-10l-1.9-2.4-35.9-.2c-19.7-.1-36.5.1-37.3.4z"/>
                <path fill="#C8B17A" d="M196.5 207.6c-3.8.8-11.7 3.8-17.4 6.6-9.1 4.4-11.6 6.2-19 13.7-9.7 9.7-14.2 16.7-18.3 28.2a75.91 75.91 0 0 0 0 49.8c4.1 11.4 8.6 18.5 17.7 27.7s18.2 15 30 19.1c7.3 2.5 9.3 2.7 22.5 2.8 12.4 0 15.6-.3 22-2.3 39.5-12.3 61.9-52.9 51-92.2-3.7-12.9-9.3-22.4-19.5-32.5-10.3-10.3-16.9-14.5-29.3-18.7-10.9-3.7-28.4-4.7-39.7-2.2zm35 14.9c14.1 4.8 27.9 16.1 34.8 28.6 7.4 13.3 9.6 30.3 5.7 45.3-2.8 11.1-7.4 18.9-16 27.7-12.2 12.2-26.1 18.1-43 18.2-10.7.1-17.3-1.5-27.9-6.5-19.9-9.5-34.1-32.2-34.1-54.8 0-13.4 6.5-30.5 15.5-40.7 5.9-6.7 17.6-14.5 25.9-17.3 12-4 28.2-4.2 39.1-.5z"/>
                <path fill="#C8B17A" d="M207 245c-1.1 1.1-2.2 3.1-2.6 4.5-.3 1.4-1.3 2.5-2.3 2.5-3.6 0-10.1 5.4-12.6 10.4-3 5.9-3.1 9.7-.6 15.2 3.8 8.3 12 12.4 25.1 12.4 5.8 0 8.5 1.4 5.9 3.1-.8.5-6.9.9-13.6.9-13.2 0-16.5.8-18.2 4.7-1.8 3.9-1.3 6.3 1.8 9.4 2.5 2.5 3.7 2.9 8.3 2.9 5.2 0 5.5.2 7 3.4 3.3 6.7 12.4 6.3 15.3-.8 1-2.3 2.3-3.6 4.2-4.1 7.1-1.7 13.2-10 13.3-17.8 0-6.7-5.9-15.2-12.2-17.6-1.5-.6-6.8-1.3-11.8-1.7-5-.4-9.3-1.1-9.6-1.5-1.1-1.9 4.3-2.9 16.1-2.9 12 0 12.7-.1 15-2.5 1.7-1.6 2.5-3.5 2.5-5.7-.1-5.9-4-8.8-11.9-8.8-3.9 0-5.1-.4-5.1-1.5 0-.9-1.1-2.7-2.5-4-3.1-3.1-8.6-3.4-11.5-.5zM394.5 230.6c-35.7 3.7-60.1 12.4-67.5 24.1l-3 4.7v106.8c0 104.5 0 107 2 110.8 4.8 9.5 20.1 17.2 43.5 21.9 16.6 3.4 32.1 4.4 56.5 3.8 29.1-.7 45.6-3.4 63-10.3 10.4-4.2 17.6-9.5 20.7-15.3l2.3-4.4V366.8c0-88.1-.2-106.4-1.4-109.2-4.6-11.1-22.7-19.7-52.1-24.7-9.3-1.6-17.3-2.2-36-2.4-13.2-.2-25.8-.1-28 .1zm52 17.9c25.5 3 49.7 11.1 47.8 16-.8 2.2-13.8 8.3-22.5 10.5-21.9 5.5-55.8 7.5-80 4.6-23.7-2.7-41.7-7.6-48.3-13.1l-3-2.5 3-2.5c3.6-3 16-7.6 26.7-10 20.1-4.4 52.8-5.7 76.3-3zM346.2 287c6.8 3 21.9 6.7 35.3 8.7 16.9 2.5 56.2 2.4 73 0 14.2-2.1 25.6-4.9 33.9-8.3 3.3-1.3 6.1-2.4 6.3-2.4.2 0 .3 3.1.3 7 0 6.9 0 6.9-3.7 9.5-5.2 3.5-20.2 8.2-34.7 10.8-16.6 3-59.1 3.2-76.1.3-17.7-2.9-32.6-7.8-37.7-12.4-1.4-1.2-1.8-3.1-1.8-8.4 0-3.7.2-6.8.4-6.8s2.4.9 4.8 2zm1.8 35c4.3 2.3 21.5 6.2 35.8 8 26.4 3.4 60.7 2.4 85.1-2.6 4.1-.9 11.6-3.1 16.8-4.9l9.3-3.3v6.7c0 5.2-.4 7.1-1.8 8.3-4.8 4.3-19.3 9.3-36.5 12.5-11.9 2.2-62.9 2.6-74.7.5-17.3-3.1-30.9-7.3-37.2-11.5-3.2-2.2-3.3-2.6-3.6-9.5l-.3-7.2 2.6 1c1.5.6 3.5 1.5 4.5 2zm14.4 38.5c36.2 9.2 95.6 7.1 125.1-4.4l7.5-2.9v6.9c0 6.5-.1 6.9-3.2 9.2-20.8 15.2-90.4 19-131.4 7.2-16.8-4.9-19.4-7.1-19.4-16.5v-6.8l6.1 2.5c3.4 1.4 10.3 3.5 15.3 4.8zm-15 29.6c26.4 11.6 90.7 13.8 127.6 4.2 5.2-1.3 11.5-3.3 13.9-4.4 2.4-1 4.7-1.9 5.2-1.9s.9 2.9.9 6.4c0 5.9-.2 6.6-3 8.9-5.5 4.7-21.3 9.5-40 12.4-16 2.4-52 2.4-68 0-15.9-2.4-29.3-6.2-36.5-10.1l-6-3.3-.3-7.1c-.2-4 .1-7.2.6-7.2s3.1.9 5.6 2.1zm.5 34.4c16.3 6.7 38.7 9.8 69.6 9.9 31.2 0 51.4-2.9 70.2-9.9 3.6-1.4 6.7-2.5 6.9-2.5.8 0 .3 12.2-.5 13.9-2.7 5-25.6 12-47.3 14.5-38.5 4.5-87.4-1.4-102.9-12.4-2.7-2-2.9-2.5-2.9-9.1 0-3.8.1-6.9.3-6.9.2 0 3.2 1.1 6.6 2.5zM352 460c28.3 10.4 83.9 12 119.6 3.5 8.7-2.1 14-3.8 21.7-7 1.5-.6 1.7 0 1.7 6.4v7.1l-4.5 2.9c-6.1 3.9-18.4 7.8-32.8 10.4-16.8 3-60.2 3.2-77.2.4-17.8-3-32.6-7.9-37.7-12.5-1.4-1.2-1.8-3.1-1.8-8.5 0-6.2.2-6.8 1.8-6.2.9.5 5.1 2 9.2 3.5z"/>
              </svg>
            </div>
            <div className="metrics">
              <h2 className="metric-heading">Total Firm Sales</h2>
              <div className="metric-inner">
                <div className="metric-values">
                  <h1 className="metric-value">{formatMoney(data.powerboost_sales_completed)}</h1>
                  {data.sales_completed !== data.powerboost_sales_completed && <h3 className="metric-sub-value">{formatMoney(data.sales_completed)}</h3> || null}
                </div>
                <div className="metric-titles">
                  <h4 className="metric-value-title">RAD Boost</h4>
                  <h4 className="metric-sub-value-title">CAD</h4>
                </div>
              </div>
            </div>
          </div>
          <div className="sales-metric">
            <div className="icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path fill="#C8B17A" d="M14.5 3.3A25.6 25.6 0 0 0 1.6 17.1C.1 21.2 0 40.5.2 258l.3 236.5 2.2 4.2c1.2 2.2 3.9 5.5 6 7.2 7.8 6.5-1 6.2 162.4 5.9l148.4-.3 4.7-2.6 4.6-2.7 7.8 1.9c13.3 3.2 30.9 4.3 44.5 3 18.8-1.9 38.3-7.2 41.7-11.4 2.1-2.5 1.1-7.7-2.1-10.1-2.5-1.8-3.1-1.9-7-.8-29.9 8.7-46.7 9.9-71 5.1-38.2-7.6-71.4-33.1-89-68.5-9.1-18.1-13.1-35.4-13-56.4.1-34.9 13.5-67.3 38.3-92 18.2-18.3 39.7-29.6 65.8-34.6 11.9-2.4 35.7-2.4 47.4-.1 51.6 10.2 91 48.7 101.9 99.8 3 13.9 3.2 38.3.5 50.9-6.7 31-23.6 58.5-46.7 76-10.4 7.9-11.9 9.5-11.9 12.8 0 3.6 3.7 7.2 7.5 7.2 7 0 32.8-23.6 43.7-40.1 10.7-16.2 17.9-33.2 22-52.2 3.1-14.6 3.1-41.8 0-56.4-6.5-29.9-20.1-54.5-41.9-75.3-24-23.1-52-35.8-86.3-39.2-11.3-1.1-23.3-.6-40.2 1.9l-3.8.5-.2-105.4-.3-105.5-3-4.9c-1.9-3-4.9-6-7.9-7.9l-4.9-3-150.6-.2L19.5 1l-5 2.3zm304.4 15.3 3.1 2.6-.2 105.9-.3 106-8.3 3.4c-4.6 1.9-10.3 4.5-12.7 5.9l-4.3 2.6-.4-15.8c-.3-15.6-.3-15.9-3.3-20.7-2.1-3.5-4.5-5.6-8-7.4l-4.9-2.6H57.5l-4 2.2c-4.6 2.4-7.6 5.5-9.9 10.1-1.5 2.9-1.6 7.9-1.4 46.6l.3 43.4 3 4.3c1.7 2.3 5 5.3 7.4 6.6l4.4 2.3h89.5c84.8 0 89.4.1 88.7 1.7-2.3 5.8-4.6 13.2-6.5 21.6l-2.2 9.7H57.3l-4.4 2.3c-2.4 1.3-5.7 4.3-7.4 6.6l-3 4.3-.3 43.4c-.3 47.9-.5 46.2 6.1 53.2 6.1 6.4 2.6 6.2 112.6 6.2h99.6l8.9 8.6c7.8 7.4 19.3 15.8 31.6 23.3l3.5 2.1H164.4c-100 0-141-.3-143.3-1.1-6.6-2.3-6.1 16.6-6.1-239.6C15 71.6 15.3 23 16.3 21c.7-1.3 2.4-3 3.7-3.7 2-1 33.5-1.3 149.2-1.3h146.6l3.1 2.6zm-39.5 197.6c1.3 1.8 1.6 5.6 1.6 20.4v18.1l-8.5 7.7c-9.4 8.5-19.6 20.8-25.3 30.3l-3.7 6.3h-91.7c-82 0-91.8-.2-93.2-1.6-1.4-1.3-1.6-6.4-1.6-40.5 0-25 .4-39.7 1-41 1-1.9 3.3-1.9 110.5-1.9h109.4l1.5 2.2zm-53.6 156.6c.4 20 4.9 38.6 13.7 57 3 6.2 6.3 12.6 7.5 14.1 1.1 1.6 2 3.2 2 3.5 0 .3-42 .6-93.2.6-73.2 0-93.8-.3-95.8-1.3l-2.5-1.3-.3-39.8c-.2-36.1-.1-40.1 1.4-41.8 1.6-1.7 5.3-1.8 84.3-1.8h82.6l.3 10.8z"/>
                <path fill="#C8B17A" d="M54.5 51.9c-5.2 2.4-10.3 7.6-11.6 12.1-.6 2.2-.9 18.8-.7 46.1l.3 42.5 2.4 3.6c2.9 4.1 7.5 7.5 12.2 8.8 5.4 1.6 219.1 1.3 224.3-.2 5.3-1.6 11.8-8.1 13.4-13.4 1.7-5.8 1.7-81 0-86.8-1.6-5.3-8.1-11.8-13.4-13.4-3.2-.9-29.8-1.2-113.5-1.2-103.8 0-109.6.1-113.4 1.9zM279 67c1.9 1.9 2 3.3 2 41s-.1 39.1-2 41c-2 2-3.3 2-109.8 2-95.9 0-108-.2-110-1.6l-2.2-1.5V68.1l2.2-1.5c2-1.4 14.1-1.6 110-1.6C275.7 65 277 65 279 67z"/>
                <path fill="#C8B17A" d="M76.1 84.6c-2.9 3.6-2.6 6 .8 9.5l3 2.9 89.6-.2 89.7-.3 1.9-2.4c2.5-3 2.4-7.6-.1-10.1-2-2-3.3-2-92.4-2H78.1l-2 2.6zM78.4 120.2c-2.2 1-4.4 4.7-4.4 7.2 0 .7.9 2.5 2.1 4l2 2.6h75.8l2-2.6c1.2-1.5 2.1-3.3 2.1-4 0-2.6-2.3-6.2-4.7-7.3-3.2-1.5-71.9-1.4-74.9.1zM177.3 120c-4.6 1.9-5.7 8.3-2 11.8 2.2 2.1 3.2 2.2 19.5 2.2 15.9 0 17.4-.2 19.2-2 2.7-2.7 2.6-8.1-.1-10.6-1.9-1.7-4-1.9-18.3-2.1-8.8-.1-17.1.2-18.3.7zM76.9 231.9c-1.7 1.3-2.3 2.9-2.3 5.6 0 2.7.6 4.3 2.3 5.6 2.2 1.8 5.9 1.9 92 1.9h89.8l2.1-2.3c3.1-3.3 2.9-8-.4-10.6-2.7-2.1-3.1-2.1-91.9-2.1-85.7 0-89.4.1-91.6 1.9zM76.1 270.6c-2.4 3-2.6 4.3-1 7.8 2 4.4 4.7 4.7 33.3 4.4 26.8-.3 26.8-.3 28.7-2.7 2.5-3 2.4-6.2-.2-9.5l-2-2.6H78.1l-2 2.6zM76.9 380.9c-3.4 3.5-3.6 5.9-.8 9.5l2 2.6h131.8l2-2.6c1.2-1.5 2.1-3.3 2.1-4 0-2.6-2.3-6.2-4.7-7.3-1.7-.8-21.5-1.1-65.9-1.1H79.8l-2.9 2.9zM77.1 417.7c-1.1 1-2.4 3.2-2.7 5-.5 2.6-.1 3.7 1.9 5.7l2.6 2.6h66.2l2.4-2.5c1.4-1.3 2.5-3.1 2.5-4 0-2.4-2.7-7.2-4.5-7.9-.9-.3-16.2-.6-34-.6-29.3 0-32.5.2-34.4 1.7zM167.5 418.5c-3.2 3.1-3.3 7.4-.2 10.3 2.3 2.1 3 2.2 28.5 2.2 24.9 0 26.3-.1 28.2-2 3-3 2.7-8.3-.6-10.9-2.5-2-3.9-2.1-28.1-2.1h-25.4l-2.4 2.5zM355 261.1c-23.3 2.8-44.6 13.4-62.1 30.8A108.36 108.36 0 0 0 264 342c-3.3 12.6-3.9 34.2-1.2 47 7.8 37.4 32.4 66.8 67 80.2 12.3 4.8 23.1 6.8 37.5 6.8 22 0 41.4-5.4 59.2-16.5 9.9-6.1 24.8-20.4 31.4-30 22.5-32.5 25-77.2 6.2-112.1-15.6-28.8-45-49.8-77.6-55.3-9.4-1.6-23.3-2-31.5-1zm38.2 17.7a93.55 93.55 0 0 1 57.7 46.4c19.2 36 11.5 82.1-18.5 110.9-11.4 10.9-21.7 16.9-37.1 21.6-8 2.4-10.3 2.7-25.8 2.7-14.8.1-18-.2-25-2.2-32-9.1-57.2-34.5-65.7-66.2-2-7.3-2.3-10.8-2.2-24.5.1-14.3.3-16.9 2.7-24.8 9.7-31.8 33.5-55.1 65.2-63.8 14.8-4 34.2-4.1 48.7-.1z"/>
                <path fill="#C8B17A" d="M366.4 292.1c-5.3 1.9-5.4 2.9-5.4 37.1V361h-53.1l-2.4 2.5c-3.2 3.1-3.2 6.9 0 10l2.4 2.5h31.4c30.3 0 31.5-.1 34.1-2.1l2.6-2v-37.5c0-22.6-.4-38.3-1-39.4-1.3-2.3-5.9-3.9-8.6-2.9z"/>
              </svg>
            </div>
            <div className="metrics">
              <h2 className="metric-heading">Total Pending Sales</h2>
              <div className="metric-inner">
                <div className="metric-values">
                  <h1 className="metric-value">{formatMoney(data.powerboost_sales_pending)}</h1>
                  {data.sales_pending !== data.powerboost_sales_pending && <h3 className="metric-sub-value">{formatMoney(data.sales_pending)}</h3> || null}
                </div>
                <div className="metric-titles">
                  <h4 className="metric-value-title">RAD Boost</h4>
                  <h4 className="metric-sub-value-title">CAD</h4>
                </div>
              </div>
            </div>
          </div>
          <div className="sales-metric">
            <div className="icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path fill="#C8B17A" d="M231.5 1.1a259 259 0 0 0-130.4 51.3 322.9 322.9 0 0 0-41.6 39.7c-31 37-50.7 82-57.7 131.9a402.2 402.2 0 0 0 0 64A257.5 257.5 0 0 0 224 510.1a396 396 0 0 0 63.5.1c29-4.1 54.8-11.8 78.9-23.5 27-13.1 45.9-26.3 67.2-47 22.2-21.5 37.9-43 51.4-70.2a247 247 0 0 0 24.2-76c3.1-20 3.1-55 0-75-4.3-27.3-11.4-50-23.7-75A236.2 236.2 0 0 0 437 75.9a232.9 232.9 0 0 0-68.5-49.5A248.4 248.4 0 0 0 292 2.5a369.9 369.9 0 0 0-60.5-1.4zm45.5 22a237.6 237.6 0 0 1 126.5 51.4 282 282 0 0 1 39.1 40.5A236.4 236.4 0 0 1 489 276.5c-3.9 43-20.4 85.8-46.4 120.5a278 278 0 0 1-39.6 40.9 237.5 237.5 0 0 1-114 49.7c-15.9 2.5-50 2.5-66 .1a233.3 233.3 0 0 1-129.3-63.6A232.6 232.6 0 0 1 24.3 289a303.4 303.4 0 0 1 0-66c6.6-43 23.6-81.7 50.2-114.5A282 282 0 0 1 115 69.4a235 235 0 0 1 162-46.3z"/>
                <path fill="#C8B17A" d="M171.5 163.7c-3 .7-7.3 5.2-8 8.3-.3 1.4-.1 4 .5 5.6a661 661 0 0 0 38.8 40.6l37.7 37.8-37.7 37.7a679.4 679.4 0 0 0-38.8 40.7c-1.5 3.9-.5 7.8 2.8 10.9 3.4 3.2 6.9 4.1 10.7 2.7 1.7-.6 19-17.1 40.8-38.8l37.7-37.7 37.8 37.7a661 661 0 0 0 40.6 38.8c4 1.5 7.8.5 10.8-2.8 3.2-3.4 4.1-7.2 2.8-10.9-.6-1.5-18.1-19.8-38.8-40.6L271.5 256l37.7-37.8c21.1-21.1 38.2-39 38.8-40.6 2.9-7.8-5.7-16.5-13.5-13.6a609 609 0 0 0-40.7 38.8L256 240.5l-37.7-37.7a757.2 757.2 0 0 0-40.6-38.8c-3-1.1-3-1.1-6.2-.3z"/>
              </svg>
            </div>
            <div className="metrics">
              <h2 className="metric-heading">Total Rescinded Sales</h2>
              <div className="metric-inner">
                <div className="metric-values">
                  <h1 className="metric-value">{formatMoney(data.powerboost_sales_rescinded)}</h1>
                  {data.sales_rescinded !== data.powerboost_sales_rescinded && <h3 className="metric-sub-value">{formatMoney(data.sales_rescinded)}</h3> || null}
                </div>
                <div className="metric-titles">
                  <h4 className="metric-value-title">RAD Boost</h4>
                  <h4 className="metric-sub-value-title">CAD</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="pure-u-1 pure-u-lg-1-2 pure-u-xl-1-3">
          <div>
            <h2 className="metric-heading">Average Price of Units Sold</h2>
            <div className="metric-value large">{formatMoney(data.sales_average)}</div>
          </div>
          <div>
            <h2 className="metric-heading">Average Size of Units Sold</h2>
            <div className="metric-value">
              {data.avg_sq_ft && `${numberFormat.format(Math.round(parseFloat(data.avg_sq_ft)))} sq. ft.` || '-'}
            </div>
          </div>
        </div>
        <div className="pure-u-1 pure-u-lg-1-2 pure-u-xl-1-3 suite-table-wrapper">
          <SuiteTable data={data}/>
        </div>
        <div className="pure-u-1 pure-u-lg-1-2 pure-u-xl-1-3">
          <h2 className="metric-heading">Leaderboard</h2>
          <div className="leaderboard-table-wrapper">
            <LeaderboardTable data={data}/>
          </div>
        </div>
      </div>
    </div>
  );
};

export default hot(module)(DashboardPage);
